import { parse, unparse } from 'papaparse'

import { LandingPageCategory } from '@resources/lms-management/landing-page/types'
import { sanitizeLandingPageTargeting } from '@resources/lms-management/landing-page/utils'

const whitelistUserCsvHeader = 'user_emails'
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type SelfEnrollmentConfigFormValues = {
  quotaMaxProgram: number
  quotaMaxKelas: number
  isAutoApprove: boolean
  blueprintCourseId?: string
  title?: string
  description?: string
  detail?: string
  badge?: string
  category?: LandingPageCategory
  provinceCodes?: string[]
  whitelistEmailDomains?: string[]
}

export type SelfEnrollmentConfigPayload = {
  diklatLandingPage: {
    blueprintCourseId?: string
    title?: string
    description?: string
    detail?: string
    badge?: string
    category?: LandingPageCategory
    provinceCodes?: string[]
    whitelistEmailDomains?: string[]
  }
  configuration: {
    quotaMaxProgram: number
    quotaMaxKelas: number
    isAutoApprove: boolean
  }
}

export const normalizeTotalWhitelistedUsers = (value?: string | number) => {
  const totalWhitelistedUsers = Number(value)

  return Number.isFinite(totalWhitelistedUsers) ? totalWhitelistedUsers : 0
}

const normalizeCell = (value: unknown) => String(value ?? '').trim()

const normalizeHeader = (value: unknown) =>
  normalizeCell(value).replace(/^\uFEFF/, '')

type WhitelistUserCsvAnalysis = {
  error?: string
  headers?: string[]
  rows: string[][]
  hasEmptyRows: boolean
  linebreak?: string
}

export type PreparedWhitelistUserCsvFile = {
  file?: File
  error?: string
}

const analyzeWhitelistUserCsvText = (
  csvText: string,
): WhitelistUserCsvAnalysis => {
  if (!csvText.trim()) {
    return {
      error:
        'Berkas CSV harus memiliki header user_emails dan minimal satu email',
      rows: [],
      hasEmptyRows: false,
    }
  }

  const result = parse<string[]>(csvText, {
    delimiter: ',',
    skipEmptyLines: false,
  })

  if (result.errors.length > 0) {
    return {
      error: 'Format CSV tidak valid',
      rows: [],
      hasEmptyRows: false,
    }
  }

  const [headers, ...rows] = result.data

  if (
    !headers ||
    headers.length !== 1 ||
    normalizeHeader(headers[0]) !== whitelistUserCsvHeader
  ) {
    return {
      error: 'CSV harus memiliki tepat satu header: user_emails',
      rows: [],
      hasEmptyRows: false,
    }
  }

  const nonEmptyRows = rows.flatMap((row, index) => {
    if (!row || row.every((cell) => normalizeCell(cell) === '')) {
      return []
    }

    return [{ row, rowNumber: index + 2 }]
  })

  if (nonEmptyRows.length === 0) {
    return {
      error: 'CSV harus memiliki minimal satu email',
      rows: [],
      hasEmptyRows: rows.length > 0,
    }
  }

  for (const { row, rowNumber } of nonEmptyRows) {
    if (row.length !== 1) {
      return {
        error: `Baris ${rowNumber} hanya boleh berisi satu email`,
        rows: [],
        hasEmptyRows: false,
      }
    }

    const email = normalizeCell(row[0])

    if (!email) {
      return {
        error: `Email pada baris ${rowNumber} wajib diisi`,
        rows: [],
        hasEmptyRows: false,
      }
    }

    if (!emailRegex.test(email)) {
      return {
        error: `Email pada baris ${rowNumber} tidak valid`,
        rows: [],
        hasEmptyRows: false,
      }
    }
  }

  return {
    headers,
    rows: nonEmptyRows.map(({ row }) => row),
    hasEmptyRows: nonEmptyRows.length !== rows.length,
    linebreak: result.meta.linebreak,
  }
}

export const validateWhitelistUserCsvText = (csvText: string) =>
  analyzeWhitelistUserCsvText(csvText).error

export const prepareWhitelistUserCsvFile = async (
  file: File,
): Promise<PreparedWhitelistUserCsvFile> => {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { error: 'Hanya dapat mengunggah berkas CSV' }
  }

  const csvText = await file.text()
  const analysis = analyzeWhitelistUserCsvText(csvText)

  if (analysis.error) {
    return { file, error: analysis.error }
  }

  if (!analysis.hasEmptyRows) {
    return { file }
  }

  const sanitizedCsvText = unparse(
    [analysis.headers as string[], ...analysis.rows],
    { newline: analysis.linebreak || '\n' },
  )

  return {
    file: new File([sanitizedCsvText], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    }),
  }
}

export const buildSelfEnrollmentConfigPayload = (
  values: SelfEnrollmentConfigFormValues,
): SelfEnrollmentConfigPayload => {
  const {
    category,
    provinceCodes,
    whitelistEmailDomains,
    blueprintCourseId,
    title,
    description,
    detail,
    badge,
    quotaMaxProgram,
    quotaMaxKelas,
    isAutoApprove,
  } = values

  const targeting = sanitizeLandingPageTargeting({
    category,
    provinceCodes,
    whitelistEmailDomains,
  })

  return {
    diklatLandingPage: {
      blueprintCourseId,
      title,
      description,
      detail,
      badge,
      ...targeting,
    },
    configuration: {
      quotaMaxProgram: Number(quotaMaxProgram),
      quotaMaxKelas: Number(quotaMaxKelas),
      isAutoApprove: !!isAutoApprove,
    },
  }
}

export const buildSelfEnrollmentConfigFormData = (
  values: SelfEnrollmentConfigFormValues,
  whitelistUserCsvFile?: File,
) => {
  const payload = buildSelfEnrollmentConfigPayload(values)
  const formData = new FormData()

  formData.append(
    'diklatLandingPage',
    JSON.stringify(payload.diklatLandingPage),
  )
  formData.append('configuration', JSON.stringify(payload.configuration))

  if (values.category === 'WHITELIST_USER' && whitelistUserCsvFile) {
    formData.append('file', whitelistUserCsvFile)
  }

  return formData
}
